/**
===================================================
Module: frontend.src.components.visualizer.KeyframeViewer.tsx
Description: Keyframe viewer component with 360-degree panorama support
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useRef, useEffect, useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { ReactPhotoSphereViewer } from "react-photo-sphere-viewer";
import { useVisualizer } from "../../contexts/VisualizerContext";
import { apiService } from "../../services/api";
import { LoadingPhase } from "../../types/visualizer";
import "react-photo-sphere-viewer/dist/index.css";

const KeyframeViewer: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { state, addSuccess, addError } = useVisualizer();
  const { sessionData, currentKeyframeIndex, loadingPhase } = state;

  const photoSphereRef = useRef<any>(null);
  const [showKeyframeDropdown, setShowKeyframeDropdown] = useState(false);
  const [isExportingKeyframe, setIsExportingKeyframe] = useState(false);
  const [isNavigationLoading, setIsNavigationLoading] = useState(false);

  // Get current keyframe
  const currentKeyframe = sessionData?.keyframes[currentKeyframeIndex];

  // Helper function to set panorama from thumbnail (reusing carousel images)
  const setPanoramaFromThumbnail = useCallback(
    (keyframe: any, thumbnailElement: HTMLImageElement) => {
      // Set orientation based on VSLAM data
      const data = sessionData?.parsedMapData;
      if (data?.keyframes) {
        const kf = data.keyframes.find(
          (k: any) =>
            k.keyframe_id?.toString() === keyframe.keyframe_id?.toString(),
        );

        if (kf && kf.camera_pose?.camera_orientation?.quaternion) {
          const [x, y, z, w] = kf.camera_pose.camera_orientation.quaternion;

          // Convert quaternion to Euler angles
          const euler = {
            x: Math.atan2(2 * (w * x + y * z), 1 - 2 * (x * x + y * y)),
            y: Math.asin(2 * (w * y - z * x)),
            z: Math.atan2(2 * (w * z + x * y), 1 - 2 * (y * y + z * z)),
          };

          // Convert to Photo Sphere Viewer coordinates (longitude/latitude)
          const longitude = (euler.y * 180) / Math.PI + 180; // Convert to 0-360 range
          const latitude = (euler.x * 180) / Math.PI;

          // Convert the loaded image to a blob URL to avoid external requests
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          if (ctx) {
            canvas.width = thumbnailElement.naturalWidth;
            canvas.height = thumbnailElement.naturalHeight;
            ctx.drawImage(thumbnailElement, 0, 0);

            canvas.toBlob(
              (blob) => {
                if (blob) {
                  // Create a blob URL from the loaded image
                  const blobUrl = URL.createObjectURL(blob);

                  // Use the blob URL with PhotoSphereViewer
                  if (photoSphereRef.current) {
                    photoSphereRef.current.setPanorama(blobUrl, {
                      longitude: longitude,
                      latitude: latitude,
                    });

                    // Clean up the blob URL after a delay to free memory
                    setTimeout(() => {
                      URL.revokeObjectURL(blobUrl);
                    }, 1000);
                  }

                  // Navigation loading complete
                  setIsNavigationLoading(false);
                } else {
                  setIsNavigationLoading(false);
                }
              },
              "image/jpeg",
              0.9,
            );
          } else {
            setIsNavigationLoading(false);
          }
        } else {
          setIsNavigationLoading(false);
        }
      } else {
        setIsNavigationLoading(false);
      }
    },
    [sessionData?.parsedMapData],
  );

  // Update panorama when keyframe changes
  useEffect(() => {
    if (!currentKeyframe) return;

    // Set navigation loading if we're navigating
    if (loadingPhase === LoadingPhase.NAVIGATING) {
      setIsNavigationLoading(true);
    }

    // Wait for thumbnail to be loaded in carousel
    const waitForThumbnail = () => {
      const thumbnailElement = document.getElementById(
        `thumb-${currentKeyframe.keyframe_id}`,
      ) as HTMLImageElement;
      if (
        thumbnailElement &&
        thumbnailElement.complete &&
        thumbnailElement.naturalWidth > 0
      ) {
        setPanoramaFromThumbnail(currentKeyframe, thumbnailElement);
        // Clear navigation loading once panorama is set
        setIsNavigationLoading(false);
      } else {
        setTimeout(waitForThumbnail, 100);
      }
    };

    // Start waiting for thumbnail to be ready
    setTimeout(waitForThumbnail, 100);
  }, [currentKeyframe, loadingPhase, setPanoramaFromThumbnail]);

  // Also clear loading when phase changes back to READY
  useEffect(() => {
    if (loadingPhase === LoadingPhase.READY) {
      setIsNavigationLoading(false);
    }
  }, [loadingPhase]);

  // Handle keyframe export
  const handleKeyframeExport = async (frameType: "2d" | "3d") => {
    if (!sessionId || !currentKeyframe) return;

    setIsExportingKeyframe(true);
    setShowKeyframeDropdown(false);

    try {
      await apiService.exportKeyframe(
        sessionId,
        currentKeyframe.filename,
        frameType,
      );
      addSuccess(
        `ダウンロードが開始されました`,
      );
    } catch (error) {
      addError(
        `ダウンロードに失敗しました`,
      );
    } finally {
      setIsExportingKeyframe(false);
    }
  };

  // Close keyframe dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".keyframe-menu-container")) {
        setShowKeyframeDropdown(false);
      }
    };

    if (showKeyframeDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showKeyframeDropdown]);

  // Show no keyframe message if no keyframes available
  if (!sessionData?.keyframes || sessionData.keyframes.length === 0) {
    return (
      <div className="keyframe-image-container">
        <div id="noKeyframeMessage" className="no-keyframe-message">
          {sessionId
            ? "Loading keyframes..."
            : "Select a session to view keyframes"}
        </div>
      </div>
    );
  }

  return (
    <div className="keyframe-image-container">
      <div className="keyframe-image-with-overlay">
        {isNavigationLoading && (
          <div className="keyframe-loading-overlay">
            <div className="keyframe-loading-spinner" />
            <div className="keyframe-loading-text">キーフレームを読み込み中...</div>
          </div>
        )}

        <ReactPhotoSphereViewer
          src=""
          height="400px"
          width="100%"
          ref={photoSphereRef}
          lang={{
            zoomIn: "拡大",
            zoomOut: "縮小",
            fullscreen: "全画面表示"
          }}
        />
      </div>

      <div className="keyframe-menu-container">
        <button
          className={`keyframe-menu-button ${isExportingKeyframe ? "exporting" : ""}`}
          onClick={() => setShowKeyframeDropdown(!showKeyframeDropdown)}
          disabled={isExportingKeyframe || !currentKeyframe}
          title="キーフレームのダウンロード"
        >
          {isExportingKeyframe ? (
            <div className="keyframe-menu-spinner" />
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3,6H21V8H3V6M3,11H21V13H3V11M3,16H21V18H3V16Z" />
            </svg>
          )}
        </button>

        {showKeyframeDropdown && (
          <div className="keyframe-menu-dropdown">
            <button
              className="keyframe-menu-option"
              onClick={() => handleKeyframeExport("3d")}
            >
              <span className="keyframe-menu-icon">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" />
                </svg>
              </span>
              <div className="keyframe-menu-text-container">
                <span className="keyframe-menu-text">パノラマ画像を出力</span>
              </div>
            </button>
            <button
              className="keyframe-menu-option"
              onClick={() => handleKeyframeExport("2d")}
            >
              <span className="keyframe-menu-icon">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" />
                </svg>
              </span>
              <div className="keyframe-menu-text-container">
                <span className="keyframe-menu-text">画像を出力</span>
              </div>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default KeyframeViewer;
